import { memo, useState, useEffect, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import { useStore } from 'store/StoreContext';
import { GetLoginDisplayItemResData, callGetLoginDisplayItemApi } from 'requests/RequestLogin';
import { callSendMailApi } from 'requests/RequestUser';
import { GetSiteResData, callGetSiteApi } from 'requests/RequestSite';
import { RoutePath } from 'consts/RoutePath';
import { CircularProgress } from '@material-ui/core';
import { convertObjectToGetParameter } from 'common/ObjectToGetParameterConverter';
import { HttpStatus } from 'consts/HttpStatus';
import { unescapeHtml } from 'common/HtmlEscaper';
import { ReactError } from 'consts/ReactError';
import { ResponseData } from 'hooks/UseNext4To5';
import { convertObjectToGetParameterWithEncode } from 'common/ObjectToGetParameterConverter';
import { hasScope } from 'utils/oidcUtils';
import { GroupAuth } from 'consts/GroupAuthConst';
import classes from './override.module.scss';

type Props = {};

export const UserRegister5Template = memo((_props: Props) => {
	const history = useHistory<ResponseData>();
	const historyRedirect = useHistory();
	const dto = history.location.state;
	const [store] = useStore(); // eslint-disable-line
	const [actionError, setActionError] = useState('');
	const isActionError = actionError !== '';

	const [displayItem, setDisplayItem] = useState<GetLoginDisplayItemResData>(undefined);
	const [site, setSite] = useState<GetSiteResData>(undefined);
	const [displayItemLoading, setDisplayItemLoading] = useState(false);
	useEffect(() => {
		if (store.registerUserData.isV2ParamsOk) {
			const execute = async () => {
				setDisplayItemLoading(true);
				const results = await Promise.all([
					callGetLoginDisplayItemApi({
						gid_dp: store.authorizeV2Data.gid_dp,
						site_cd: store.authorizeV2Data.client_id,
					}),
					callGetSiteApi(store.authorizeV2Data.client_id),
				]);

				const isError = results.some((r) => r.status !== HttpStatus.OK || !r.data);
				if (isError) {
					setActionError(ReactError.E0008);
					history.push(RoutePath.ERROR);
					setDisplayItemLoading(false);
					return;
				}

				const [getDisplayItemResult, getSiteResult] = results;

				setDisplayItem(getDisplayItemResult.data);
				setSite(getSiteResult.data);
				setDisplayItemLoading(false);
			};
			execute();
		}
	}, [store.authorizeV2Data.gid_dp, store.registerUserData.isV2ParamsOk]);

	// 登録完了メール送信
	const [sendMailLoading, setSendMailLoading] = useState(true);
	useEffect(() => {
		const execute = async () => {
			const result = await callSendMailApi({ send_type: 1 });
			if (result.status !== HttpStatus.OK) {
				setActionError(ReactError.E9999);
				setSendMailLoading(false);
				history.push(RoutePath.ERROR);
				return;
			}
			setSendMailLoading(false);
		};
		execute();
	}, []);

	// OIDCの場合の属性提供同意画面遷移
	const transAttrAgree = () => {
		const getParameters = convertObjectToGetParameterWithEncode(
			store.authorizeV2Data,
			'code_challenge',
			'redirect_uri',
			'nonce',
		);
		history.push(`${RoutePath.ATTRIBUTE_AGREEMENT}?${getParameters}`, dto);
	};

	// 戻るボタンのonClickで呼ぶ
	const [returnTransitioning, setReturnTransitioning] = useState(false);
	const returnTop = () => {
		setReturnTransitioning(true);
		if (store.registerUserData.isV1ParamsOk) {
			const getParameters = convertObjectToGetParameter(dto.v1);
			// リダイレクト先urlがエンコードされたままなのでデコードしてからリダイレクトする
			window.location.href = `${decodeURIComponent(store.authorizeV1Data.burl)}?${getParameters}`;
		} else if (store.registerUserData.isV2ParamsOk) {
			const getParameters = convertObjectToGetParameter(dto.v2);
			// リダイレクト先urlがエンコードされたままなのでデコードしてからリダイレクトする
			window.location.href = `${
				store.authorizeV2Data.redirect_uri
					? decodeURIComponent(store.authorizeV2Data.redirect_uri)
					: displayItem.redirect_url
			}?${getParameters}`;
		} else {
			history.push(RoutePath.MY_PAGE);
		}
	};
	const memberRegister = () => {
		historyRedirect.push({ pathname: RoutePath.MEMBER_INPUT, state: { fromPage: 'memberAdd' } });
	};

	const scrollRef = useRef(null);
	useEffect(() => {
		scrollRef.current.scrollIntoView();
		document.title = 'GakkenID新規登録｜登録完了';
	}, []);

	const errorRef = useRef(null);
	useEffect(() => {
		if (store.registerUserData.actionError !== '' || isActionError) {
			errorRef.current.scrollIntoView();
		}
	}, [store.registerUserData.actionError, actionError]);

	return (
		<main>
			<div className="section mypage">
				<div className="section-in" ref={scrollRef}>
					<div className="steps">
						<img className="pc" src="images/img-steps-05.svg" alt="登録完了" />
						<img className="sp" src="images/img-steps-05_sp.svg" alt="" />
					</div>
					<div className="step-title sp">登録完了</div>

					{store.registerUserData.actionError !== '' || isActionError ? (
						<div className="error-box" ref={errorRef}>
							<p>{actionError}</p>
						</div>
					) : null}

					<div className="thankyou-box">
						<img src="../images/img-thankyou.svg" alt="Thank You." />
					</div>

					<p>
						GakkenIDの会員登録が完了いたしました。
						<br />
						ご登録完了メールをお送りしておりますのでご確認ください。
						{store.authorizeV2Data.gid_reg_act == 'force_child' ? (
							<>
								<br />
								{`続けてGakkenこどもIDの登録を行います。`}
							</>
						) : null}
					</p>
					<div className="btn-box">
						{sendMailLoading || displayItemLoading || returnTransitioning ? (
							<CircularProgress />
						) : (
							<>
								{hasScope(store.authorizeV2Data.scope) &&
								Boolean(site?.need_claim_agreement_flg) &&
								!(store.authorizeV2Data.gid_reg_act == 'force_child') ? (
									<div className="btn gr">
										<button onClick={transAttrAgree}>
											{displayItem?.member_regist_complete ? displayItem?.member_regist_complete : `次へ`}
										</button>
									</div>
								) : (
									<>
										{store.authorizeV2Data.gid_reg_act == 'force_child' ? (
											<div className="btn gr">
												<button onClick={memberRegister}>次へ</button>
											</div>
										) : (
											<>
												{store.registerUserData.isV1ParamsOk ? (
													<div className="btn gr">
														<button
															onClick={returnTop}
															dangerouslySetInnerHTML={{ __html: unescapeHtml(store.authorizeV1Data.bsn) }}
														/>
													</div>
												) : null}
												{store.registerUserData.isV2ParamsOk ? (
													<div className="btn gr">
														<button onClick={returnTop}>
															{displayItem?.member_regist_complete ? displayItem?.member_regist_complete : `次へ`}
														</button>
													</div>
												) : null}
												{!store.registerUserData.isV1ParamsOk && !store.registerUserData.isV2ParamsOk ? (
													<div className="btn gr">
														<button onClick={returnTop}>マイページTOPへ戻る</button>
													</div>
												) : null}
											</>
										)}
									</>
								)}
							</>
						)}
					</div>
					{store.authorizeV2Data.gid_reg_act == 'force_child' ? (
						<div className={classes.memberInfoChgIdDiv}>
							{`「Gakken${GroupAuth.MEMBER.name}」については`}
							<a href="/faq.html#h" target="_blank">
								こちら
							</a>
						</div>
					) : null}
					{store.authorizeV2Data.gid_reg_act == 'child' ? (
						<>
							<div className="section-head" style={{ marginTop: 20 }}>
								<span className={classes.sectionHeadH1}>続けてGakkenこどもIDを登録する</span>
							</div>
							<p>
								ご登録いただいたGakkenIDの家族に追加する形で、お子様用のID（GakkenこどもID）を登録することができます。
								<br />
								GakkenこどもIDの登録にはメールアドレスは必要ありません。
								<br />
								※サービス利用時に決済ができないなど、一部機能に制限があります
								<br />
								※後でGakkenIDマイページから登録することも可能です
							</p>
							<div className="btn-box">
								<div className="btn gr">
									<button onClick={memberRegister}>登録</button>
								</div>
							</div>
							<div className={classes.memberInfoChgIdDiv}>
								{`「Gakken${GroupAuth.MEMBER.name}」については`}
								<a href="/faq.html#h" target="_blank">
									こちら
								</a>
							</div>
						</>
					) : null}
				</div>
			</div>
		</main>
	);
});
